import { createFileRoute, Link, notFound } from '@tanstack/react-router';
import {
  Box,
  Container,
  Typography,
  Chip,
  Stack,
  Button,
  Divider,
  Avatar,
  Paper,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { getResearchPost, getResearchImageUrl } from '../../lib/research';
import { getRelatedPosts } from '../../lib/related';
import { markdownBodyStyles, createMarkdownComponents } from '../../lib/markdown';
import { useEffect, useMemo, useRef } from 'react';
import { useSEO, useBreadcrumbSchema, useBlogPostSchema, BlogPostSchema } from '../../lib/seo';
import { usePostHog } from '../../components/widgets/PostHogProvider';

export const Route = createFileRoute('/resources/research/$slug')({
  component: ResearchPostPage,
});

function ResearchPostPage() {
  const { slug } = Route.useParams();
  const post = getResearchPost(slug);
  const { capture } = usePostHog();
  const hasTrackedView = useRef(false);

  // SEO for research post
  useSEO(post ? {
    title: post.title,
    description: post.excerpt,
    canonical: `/resources/research/${slug}`,
    ogType: 'article',
    ogImage: post.image,
    article: {
      publishedTime: post.date,
      author: post.author,
      tags: post.tags,
    }
  } : { title: 'Research' });

  useBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Research', url: '/resources/research' },
    ...(post ? [{ name: post.title, url: `/resources/research/${slug}` }] : [])
  ]);

  // BlogPosting schema (works for research articles too)
  const researchSchema: BlogPostSchema | null = useMemo(() => {
    if (!post) return null;
    return {
      title: post.title,
      description: post.excerpt,
      datePublished: post.date,
      author: post.author,
      authorRole: post.authorRole,
      image: post.image,
      url: `https://www.neumind.io/resources/research/${slug}`,
      tags: post.tags,
    };
  }, [post, slug]);

  useBlogPostSchema(researchSchema);

  // Track research post view
  useEffect(() => {
    if (post && !hasTrackedView.current) {
      hasTrackedView.current = true;
      capture('research_post_viewed', {
        post_slug: slug,
        post_title: post.title,
        post_author: post.author,
        post_tags: post.tags,
        post_date: post.date,
      });
    }
  }, [post, slug, capture]);

  if (!post) {
    throw notFound();
  }

  const relatedPosts = getRelatedPosts('research', slug, post.tags, post.related);

  return (
    <>
      {/* Hero with image */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: 300, md: 400 },
          bgcolor: 'grey.900',
          overflow: 'hidden',
        }}
      >
        {post.image && (
          <Box
            component="img"
            src={post.image}
            alt={post.title}
            sx={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: 0.4,
            }}
          />
        )}
        <Container
          maxWidth="md"
          sx={{
            position: 'relative',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            pb: 4,
          }}
        >
          <Button
            component={Link}
            to="/resources/research"
            startIcon={<ArrowBackIcon />}
            sx={{ color: 'white', alignSelf: 'flex-start', mb: 2 }}
          >
            Back to Research
          </Button>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            {post.tags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', borderColor: 'rgba(255,255,255,0.3)', borderWidth: 1, borderStyle: 'solid' }}
              />
            ))}
          </Stack>
          <Typography variant="h1" sx={{ color: 'white', mb: 2 }}>
            {post.title}
          </Typography>
          <Stack direction="row" spacing={3} alignItems="center">
            <Stack direction="row" spacing={1} alignItems="center">
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                {post.author[0]}
              </Avatar>
              <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                {post.author}{post.authorRole && <Typography component="span" variant="body2" sx={{ color: 'grey.400', fontWeight: 400 }}>{' · '}{post.authorRole}</Typography>}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <CalendarTodayIcon sx={{ fontSize: 16, color: 'grey.400' }} />
              <Typography variant="body2" sx={{ color: 'grey.400' }}>
                {new Date(post.date).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
              </Typography>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Box sx={markdownBodyStyles}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={createMarkdownComponents(getResearchImageUrl)}
          >
            {post.content}
          </ReactMarkdown>
        </Box>

        <Divider sx={{ my: 6 }} />

        {/* Related posts */}
        {relatedPosts.length > 0 && (
          <Box>
            <Typography variant="h4" sx={{ mb: 3 }}>
              Related Research
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
              {relatedPosts.map((related) => (
                <Paper
                  key={`${related.type}:${related.slug}`}
                  component={Link}
                  to={related.url}
                  elevation={0}
                  onClick={() => {
                    capture('related_research_clicked', {
                      source_post_slug: slug,
                      source_post_title: post.title,
                      related_post_slug: related.slug,
                      related_post_title: related.title,
                    });
                  }}
                  sx={{
                    flex: 1,
                    p: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    textDecoration: 'none',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  <Typography variant="h5" sx={{ mb: 1, color: 'text.primary' }}>
                    {related.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {related.excerpt}
                  </Typography>
                </Paper>
              ))}
            </Stack>
          </Box>
        )}
      </Container>
    </>
  );
}
